import React, { useState } from 'react';
import { footerData } from '../data/mock';
import { Instagram, Facebook, Twitter, Mail, Phone, MapPin, Send, Store, ArrowRight } from 'lucide-react';

const socialIcons = {
  Instagram: Instagram,
  Facebook: Facebook,
  Twitter: Twitter,
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 3000);
    }
  };

  return (
    <footer id="contact" className="relative bg-[#1e1919] pt-24 pb-8 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-1 italian-stripe" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* CTA Section */}
        <div className="text-center mb-20">
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            A Luxury Gourmet Revolution
            <span className="block gradient-text">At a Fast-Food Price</span>
          </h2>
          <p className="text-lg text-[#bbb5ae] max-w-2xl mx-auto mb-10">
            Experience the future of pizza. Join thousands who have already discovered
            the perfect balance of health, taste, and indulgence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#menu"
              className="bg-[#c9a962] text-[#1e1919] px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-white hover:shadow-xl"
            >
              View Menu
            </a>
            <a
              href="tel:+971559472027"
              className="border-2 border-white/30 text-white px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-white hover:text-[#1e1919]"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* Newsletter */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 lg:p-12 mb-20">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-2xl font-bold text-white mb-2">
                Stay Updated
              </h3>
              <p className="text-[#bbb5ae]">
                Subscribe to our newsletter for exclusive offers and new menu updates.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#c9a962] transition-colors"
                required
              />
              <button
                type="submit"
                className="px-6 py-4 bg-[#c9a962] text-[#1e1919] font-semibold rounded-lg transition-all duration-300 hover:bg-white flex items-center gap-2"
              >
                <Send size={18} />
                <span className="hidden sm:inline">Subscribe</span>
              </button>
            </form>
            {submitted && (
              <p className="lg:col-span-2 text-center text-[#c9a962] font-medium">
                Thank you for subscribing!
              </p>
            )}
          </div>
        </div>

        {/* Franchise Opportunities Call-out */}
        <div
          data-testid="franchise-callout"
          className="relative overflow-hidden rounded-2xl border border-[#c9a962]/30 bg-gradient-to-r from-[#c9a962]/15 via-[#1e1919] to-[#1e1919] p-8 lg:p-12 mb-20"
        >
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#c9a962]/15 border border-[#c9a962]/30 flex items-center justify-center">
                <Store size={26} className="text-[#c9a962]" />
              </div>
              <div>
                <span className="inline-block text-[#c9a962] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Investor Opportunity
                </span>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                  Own a CRISPYzza Franchise
                </h3>
                <p className="text-[#bbb5ae] max-w-xl">
                  Join the Italian gourmet revolution in the Gulf. Turnkey formats,
                  Halal excellence, and Chef Francesco's signature crisp — now open for partners.
                </p>
              </div>
            </div>
            <a
              href="https://crispy-franchise.emergent.host"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-franchise-btn"
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 bg-[#c9a962] text-[#1e1919] px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-white hover:shadow-xl hover:-translate-y-0.5"
            >
              Explore Franchising
              <ArrowRight size={18} />
            </a>
          </div>
        </div>

        {/* Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <span className="font-display text-3xl font-bold text-white">CRISPYzza</span>
              <span className="block text-[#c9a962] text-sm mt-1">by Chef Francesco</span>
            </div>
            <p className="text-[#bbb5ae] leading-relaxed max-w-md mb-6">
              {footerData.description}
            </p>
            <div className="flex gap-4">
              {footerData.social.map((social) => {
                const IconComponent = socialIcons[social.name];
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:bg-[#c9a962] hover:text-[#1e1919]"
                    aria-label={social.name}
                  >
                    {IconComponent && <IconComponent size={20} />}
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-bold text-white mb-6">
              Quick Links
            </h4>
            <ul className="space-y-4">
              {['About Us', 'Our Menu', 'The Chef', 'Locations', 'Franchise'].map((link) => (
                <li key={link}>
                  <a
                    href="#"
                    className="text-[#bbb5ae] hover:text-[#c9a962] transition-colors"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg font-bold text-white mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`mailto:${footerData.contact.email}`}
                  className="flex items-center gap-3 text-[#bbb5ae] hover:text-[#c9a962] transition-colors"
                >
                  <Mail size={18} className="text-[#c9a962]" />
                  {footerData.contact.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${footerData.contact.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-[#bbb5ae] hover:text-[#c9a962] transition-colors"
                >
                  <Phone size={18} className="text-[#c9a962]" />
                  {footerData.contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#bbb5ae]">
                <MapPin size={18} className="text-[#c9a962] flex-shrink-0 mt-1" />
                <span>Dubai, United Arab Emirates</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#736c64] text-sm">
              © {new Date().getFullYear()} CRISPYzza by Chef Francesco. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-[#736c64] text-sm hover:text-[#c9a962] transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-[#736c64] text-sm hover:text-[#c9a962] transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
